import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle, ChevronDown, Clock, ListChecks, User } from 'lucide-react';
import { ProjectWithBuilding } from '../../../api/projectsApi';
import { ProjectStatus } from '../../../types/projects';
import { getAssignees, getDelegations } from '../../../types/assignees';
import { DelmomentSummary, projectDaysLeft, isUrgentProject } from '../../../Helpers/projectBoard';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';
import DelmomentProgressRing from '../../Förvaltning/components/DelmomentProgressRing';
import { useOpenPath } from '../../../hooks/useOpenPath';

interface ProjectBoardCardProps {
    project: ProjectWithBuilding;
    summary: DelmomentSummary;
    today: string;
    expanded: boolean;
    onToggle: () => void;
    getUserName: (uid: string) => string;
    /** The expanded body (a DelmomentList). */
    children: React.ReactNode;
}

/** The app's own project icon (as on the project page) */
export const PROJECT_ICON = '/Images/UI/projekt.png';

/** Route of the project's detail page. */
export function projectDetailPath(project: Pick<ProjectWithBuilding, 'buildingKey' | 'key'>): string {
    return `/building/${project.buildingKey}/projects/${project.key}`;
}

/**
 * The dates line, worded per column: planned end for ongoing, start for
 * planned, actual end for done. `late` colours it red like an overdue task.
 */
function datesLine(project: ProjectWithBuilding, today: string): { text: string; late: boolean; done: boolean } {
    switch (project.status) {
        case ProjectStatus.COMPLETED:
            return {
                text: project.actualEndDate ? `Slutfört: ${formatDateForDisplay(project.actualEndDate)}` : 'Slutfört',
                late: false,
                done: true
            };
        case ProjectStatus.PLANNED: {
            const days = project.startDate ? projectDaysLeft({ plannedEndDate: project.startDate }, today) : null;
            const when = days === null ? '' : days > 0 ? ` (om ${days} d)` : days === 0 ? ' (idag)' : ` (${-days} d sedan)`;
            return { text: `Start: ${formatDateForDisplay(project.startDate)}${when}`, late: false, done: false };
        }
        default: {
            const days = projectDaysLeft(project, today);
            if (days === null) return { text: 'Planerat klart: –', late: false, done: false };
            const end = formatDateForDisplay(project.plannedEndDate);
            if (days < 0) return { text: `Planerat klart: ${end} (${-days} d försenat)`, late: true, done: false };
            if (days === 0) return { text: `Planerat klart: ${end} (idag)`, late: false, done: false };
            return { text: `Planerat klart: ${end} (${days} d)`, late: false, done: false };
        }
    }
}

/** "2 förfallna · nästa 2026-10-14" for ongoing projects; null when nothing to say. */
function urgencyLine(project: ProjectWithBuilding, summary: DelmomentSummary): string | null {
    if (project.status === ProjectStatus.COMPLETED) return null;
    const parts: string[] = [];
    if (summary.overdue > 0) parts.push(summary.overdue === 1 ? '1 förfallet' : `${summary.overdue} förfallna`);
    if (summary.nextDue) parts.push(`nästa ${formatDateForDisplay(summary.nextDue)}`);
    return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * A project on the board, in the Förvaltning card language: the project name
 * with the project icon (the link to the detail page), detail lines with icons,
 * the delmoment ring top-right and a labelled "Delmoment (n)" toggle row at
 * the bottom. Clicking the card opens the project; the Delmoment row expands
 * it. The building is shown by the
 * group card the project sits under (BuildingGroupCard), not here.
 */
const ProjectBoardCard: React.FC<ProjectBoardCardProps> = ({
    project,
    summary,
    today,
    expanded,
    onToggle,
    getUserName,
    children
}) => {
    const dates = datesLine(project, today);
    const urgency = urgencyLine(project, summary);
    const urgent = isUrgentProject(project, summary, today);
    const assigneeNames = [
        ...Object.keys(getAssignees(project)).map(getUserName),
        ...Object.keys(getDelegations(project)).map(uid => `${getUserName(uid)} (extern)`)
    ];
    const bodyId = `project-board-card-body-${project.key}`;

    const openPath = useOpenPath();

    // A click anywhere on the card opens the project (Cmd/Ctrl/middle click in a
    // new tab); the Delmoment row toggles the list. Keyboard users reach the
    // project through the title link, so the card itself is not focusable.
    const handleClick = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('a, button, .project-board-card__body')) return;
        openPath(projectDetailPath(project), e);
    };

    return (
        <div
            className={`management-item project-board-card${urgent ? ' management-item--overdue' : ''}${expanded ? ' project-board-card--expanded' : ''}`}
            onClick={handleClick}
            onAuxClick={handleClick}
        >
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1 me-2 project-board-card__main">
                    {/* Every card here is a project, so there is no type pill; the
                        name links to the detail page. */}
                    <Link to={projectDetailPath(project)} className="project-board-card__title mb-1">
                        <img src={PROJECT_ICON} alt="" className="project-board-card__title-icon" />
                        <span>{project.title}</span>
                    </Link>
                </div>
                <DelmomentProgressRing completed={summary.completed} total={summary.total} />
            </div>

            {dates.done ? (
                // Completed: the date in a light green pill
                <div className="mt-2">
                    <span className="project-board-done-pill">
                        <CheckCircle size={12} aria-hidden="true" />
                        {dates.text}
                    </span>
                </div>
            ) : (
                <div className={`mt-2 d-flex align-items-center gap-1 small ${dates.late ? 'text-danger' : 'text-muted'}`}>
                    <Clock size={12} />
                    <span>{dates.text}</span>
                </div>
            )}

            {urgency && (
                <div className={`mt-1 d-flex align-items-center gap-1 small ${summary.overdue > 0 ? 'text-danger' : 'text-muted'}`}>
                    <AlertTriangle size={12} />
                    <span>{urgency}</span>
                </div>
            )}

            {assigneeNames.length > 0 && (
                <div className="mt-1 d-flex align-items-center gap-1 small text-muted project-board-card__assignees">
                    <User size={12} />
                    <span>{assigneeNames.join(', ')}</span>
                </div>
            )}

            <button
                type="button"
                className="project-board-card__toggle"
                onClick={(e) => { e.stopPropagation(); onToggle(); }}
                aria-expanded={expanded}
                aria-controls={bodyId}
            >
                <ListChecks size={14} aria-hidden="true" />
                <span>Delmoment ({summary.total})</span>
                <ChevronDown size={16} className="project-board-card__chevron" aria-hidden="true" />
            </button>

            {expanded && (
                <div id={bodyId} className="project-board-card__body">
                    {children}
                </div>
            )}
        </div>
    );
};

export default ProjectBoardCard;
